import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { TextField, ButtonMenu, Button } from '@ann/web-components';
import { useDashboardFilters } from '../../hooks/useDashboardFilters';
import { useDashboardData } from '../../context';
import { DASHBOARD_STATES } from '../../constants';
import { DASHBOARD_LIST_COPY } from '../../constants/dashboardBuilderCopy';
import { flattenSectionsForFilter } from '../../utils/dashboardFormatters';
import { FiltersRow, SearchWrapper, ClearButtonWrapper } from './DashboardFilters.styled';

const CreatedFilters = () => {
  const {
    localSearch,
    setLocalSearch,
    filters,
    handleStateChange,
    handleSectionChange,
    resetFilters,
    hasActiveFilters,
  } = useDashboardFilters();

  const { sections, fetchSections } = useDashboardData();
  const [filtersKey, setFiltersKey] = useState(0);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const flatSections = useMemo(
    () => flattenSectionsForFilter(sections),
    [sections]
  );
  const { filters: filtersCopy } = DASHBOARD_LIST_COPY;
  const { stateOptions } = filtersCopy;

  const stateLabel = useMemo(() => {
    if (!filters.state) return filtersCopy.statePlaceholder;
    return stateOptions[filters.state] || filtersCopy.statePlaceholder;
  }, [filters.state, filtersCopy.statePlaceholder, stateOptions]);

  const sectionLabel = useMemo(() => {
    if (!filters.sectionId) return filtersCopy.sectionPlaceholder;
    const section = flatSections.find((s) => s.id === filters.sectionId);
    return section?.path || section?.name || filtersCopy.sectionPlaceholder;
  }, [filters.sectionId, flatSections, filtersCopy.sectionPlaceholder]);

  const handleSearchChange = useCallback((value) => {
    setLocalSearch(value ?? '');
  }, []);

  const handleReset = useCallback(() => {
    resetFilters();
    setFiltersKey((prev) => prev + 1);
  }, [resetFilters]);

  return (
    <FiltersRow>
      <SearchWrapper>
        <TextField
          iconLeft="search"
          size="small"
          placeholder={filtersCopy.searchPlaceholder}
          value={localSearch}
          onChange={handleSearchChange}
        />
      </SearchWrapper>

      <ButtonMenu
        key={`state-filter-${filtersKey}`}
        iconPosition="right"
        label={stateLabel}
        menuPosition="bottom"
        onChange={handleStateChange}
        size="medium"
        type="secondary"
        width="auto"
        usePortal={false}
      >
        <ButtonMenu.Menu>
          <ButtonMenu.Item label={stateOptions.draft} value={DASHBOARD_STATES.DRAFT}>
            {stateOptions.draft}
          </ButtonMenu.Item>
          <ButtonMenu.Item label={stateOptions.published} value={DASHBOARD_STATES.PUBLISHED}>
            {stateOptions.published}
          </ButtonMenu.Item>
          <ButtonMenu.Item label={stateOptions.archived} value={DASHBOARD_STATES.ARCHIVED}>
            {stateOptions.archived}
          </ButtonMenu.Item>
        </ButtonMenu.Menu>
      </ButtonMenu>

      <ButtonMenu
        key={`section-filter-${filtersKey}`}
        iconPosition="right"
        label={sectionLabel}
        menuPosition="bottom"
        onChange={handleSectionChange}
        size="medium"
        type="secondary"
        width="auto"
        usePortal={false}
      >
        <ButtonMenu.Menu>
          {flatSections.map((section) => (
            <ButtonMenu.Item
              key={section.id}
              label={section.path || section.name}
              value={section.id}
            >
              {section.path || section.name}
            </ButtonMenu.Item>
          ))}
        </ButtonMenu.Menu>
      </ButtonMenu>

      {hasActiveFilters && (
        <ClearButtonWrapper>
          <Button
            label={filtersCopy.clearButton}
            size="medium"
            type="secondary"
            onClick={handleReset}
          />
        </ClearButtonWrapper>
      )}
    </FiltersRow>
  );
};

export default CreatedFilters;
